import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import newTokenService from "../../features/auth/services/newTokenService";
import csrfService from "../../features/auth/api/csrfService";
import sessionSyncService from "../../features/auth/services/sessionSyncService";
import { clearSessionStorage } from "../../features/auth/services/sessionState";
import { unwrapField } from "./unwrap";
import authLogger from "../../features/auth/services/authLogger";
import { FORBIDDEN_AT_HOSPITAL } from "@contracts";
import { isCsrfRejection, refreshEndsSession, sleep, withSessionRetry } from "./retryPolicy";

// Vite expõe `import.meta.env.DEV`/`MODE`; `NODE_ENV` não existe nesse objeto.
const DEBUG = import.meta.env.DEV;

const log = {
  info: (label: string, data?: any) => DEBUG && console.log(`ℹ️ [${label}]`, data),
  success: (label: string, data?: any) => DEBUG && console.log(`✅ [${label}]`, data),
  warn: (label: string, data?: any) => DEBUG && console.warn(`⚠️ [${label}]`, data),
  error: (label: string, data?: any) => DEBUG && console.error(`❌ [${label}]`, data),
};

const api = axios.create({
  baseURL: import.meta.env.VITE_APP_NEW_BACKEND_ENDPOINT,
  timeout: 30000,
  withCredentials: true,
});

// Dedicated client for token refresh (avoids infinite recursion)
const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_APP_NEW_BACKEND_ENDPOINT,
  // 30s, igual ao cliente principal. Com 10s, um refresh lento derrubava a
  // sessão do usuário em silêncio: o backend novo escala a zero, o cold start
  // medido em produção é p50 21s / máx 25s, e a rotação REVOGA o token antigo
  // antes de emitir o novo. O cliente desistia aos 10s, o servidor concluía aos
  // ~22s, e o interceptor de resposta trata falha de refresh como logout.
  // Auditoria de 2026-08-18, achado 2.4.
  timeout: 30000,
  withCredentials: true,
});

// ========================================
// CONTROLE DE ESTADO DE REFRESH
// ========================================
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// ========================================
// CONTROLE DE AUTO-REFRESH
// ========================================
let autoRefreshInterval: NodeJS.Timeout | null = null;
const AUTO_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutos

// ========================================
// HELPERS
// ========================================
const isAuthEndpoint = (url: string = ""): boolean => {
  return url.includes("/auth/") || url.includes("/password/");
};

const isPublicEndpoint = (url: string = ""): boolean => {
  const publicRoutes = [
    "/auth/login",
    "/auth/validate",
    "/auth/send-code",
    "/auth/create-patient",
    "/auth/validate-identifier",
    "/auth/csrf",
  ];
  return publicRoutes.some((route) => url.includes(route));
};

const isCsrfProtectedEndpoint = (url: string = ""): boolean => {
  const protectedRoutes = [
    "/auth/refresh",
    "/auth/logout",
  ];
  return protectedRoutes.some((route) => url.includes(route));
};

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);
const isMutation = (method: string = ""): boolean => {
  return MUTATING_METHODS.has(method.toLowerCase());
};

// ========================================
// EVENTOS CUSTOMIZADOS
// ========================================
const notifyTokenExpired = () => {
  log.warn("EVENT_EMIT", "token:expired");
  window.dispatchEvent(new Event("token:expired"));
};

const notifySessionRevoked = () => {
  log.warn("EVENT_EMIT", "session:revoked");
  window.dispatchEvent(new Event("session:revoked"));
};

// ========================================
// REFRESH REQUEST — built on each attempt, so a retry carries the refetched CSRF token
// ========================================
const postRefresh = async () => {
  const csrfToken = csrfService.getToken();
  const refreshPayload: Record<string, string> = {};

  // Fallback para localhost/dev: envia refreshToken e csrfToken no body
  // para funcionar sem cookies httpOnly (cross-origin localhost)
  if (import.meta.env.DEV || window.location.hostname === "localhost") {
    const storedRefreshToken = newTokenService.getRefreshToken();
    if (storedRefreshToken) {
      refreshPayload.refreshToken = storedRefreshToken;
    }
    if (csrfToken) {
      refreshPayload.csrfToken = csrfToken;
    }
  }

  // Envia o access token expirando no Authorization para o backend preservar
  // o hospital ativo (activeHospitalId) na renovação — senão o refresh perde
  // o escopo de hospital (relevante quando AUTHZ_ENFORCE está ligado).
  const expiringToken = newTokenService.getAccessToken();
  const refreshHeaders: Record<string, string> = {};
  if (csrfToken) refreshHeaders["x-csrf-token"] = csrfToken;
  if (expiringToken) refreshHeaders.Authorization = `Bearer ${expiringToken}`;

  return refreshClient.post(
    "/auth/refresh",
    refreshPayload,
    {
      withCredentials: true,
      // Este teto VENCE o da instância — options por requisição sobrescreve o
      // default do `axios.create`. Trocar só lá em cima não resolveria nada,
      // e é o tipo de correção que passa em revisão parecendo feita.
      timeout: 30000,
      headers: refreshHeaders,
    }
  );
};

// ========================================
// REFRESH FUNCTION (SINGLE SOURCE OF TRUTH)
// ========================================
const performRefresh = async (): Promise<string> => {
  if (isRefreshing && refreshPromise) {
    log.info("REFRESH_WAITING", "Aguardando refresh em andamento");
    return refreshPromise;
  }

  isRefreshing = true;
  log.info("REFRESH_START", "Iniciando refresh do token");
  authLogger.log("refresh", "→ POST /auth/refresh (auto)", {
    hasCsrf: !!csrfService.getToken(),
  });

  refreshPromise = (async () => {
    try {
      if (!csrfService.getToken()) {
        await csrfService.fetchToken();
      }
      // One retry on a stale CSRF copy or a refresh another tab/portal is doing (retryPolicy.ts).
      const response = await withSessionRetry(() => postRefresh(), {
        refetchCsrf: () => csrfService.fetchToken(),
        wait: sleep,
      });

      // Suporte a envelope BaseController.ok ou jsonResponse direto
      const newToken =
        unwrapField<string>(response.data, "accessToken") ??
        unwrapField<string>(response.data, "access_token") ??
        unwrapField<string>(response.data, "token");
      const expiresAt = unwrapField<number | string>(response.data, "expiresAt");

      if (!newToken) {
        throw new Error("Refresh retornou token inválido");
      }

    newTokenService.setTokens(newToken, expiresAt as any);

    // Salva refresh token do body como fallback para localhost/dev
    const newRefreshToken =
      unwrapField<string>(response.data, "refreshToken") ??
      unwrapField<string>(response.data, "refresh_token");
    if (newRefreshToken) {
      newTokenService.setRefreshToken(newRefreshToken);
    }

    // Atualiza CSRF token da resposta (envelope-tolerant)
    csrfService.setFromResponse(response.data);

    authLogger.log("refresh", "← /auth/refresh ok", {
      httpStatus: response.status,
      hasNewToken: !!newToken,
      hasNewCsrf: !!unwrapField<string>(response.data, "csrfToken"),
    });

    api.defaults.headers.common.Authorization = `Bearer ${newToken}`;

      // Notifica outras abas que o token foi renovado
      sessionSyncService.emit("TOKEN_REFRESHED", { expiresAt });

      log.success("REFRESH_SUCCESS", {
        expiresIn: newTokenService.getTokenTimeLeft(),
      });

      return newToken;
    } catch (error: any) {
      log.error("REFRESH_FAILED", {
        status: error.response?.status,
        message: error.message,
      });
      authLogger.error("refresh", "auto-refresh falhou", {
        httpStatus: error.response?.status,
        code: error.response?.data?.code,
        message: error.response?.data?.message ?? error.message,
      });

      if (refreshEndsSession(error)) {
        // O servidor recusou o refresh: a sessão acabou de vez (não é falha de rede).
        clearSessionStorage();
        stopAutoRefresh();
        notifyTokenExpired();
      }

      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ========================================
// AUTO-REFRESH
// ========================================
export const startAutoRefresh = () => {
  if (autoRefreshInterval) {
    clearInterval(autoRefreshInterval);
  }

  log.success("AUTO_REFRESH_START", `Intervalo: ${AUTO_REFRESH_INTERVAL / 60000}min`);

  autoRefreshInterval = setInterval(async () => {
    try {
      if (!newTokenService.hasValidToken()) {
        log.info("AUTO_REFRESH_SKIP", "Token inválido ou ausente");
        stopAutoRefresh();
        return;
      }

      if (newTokenService.isTokenExpiringSoon(5 * 60)) {
        log.info("AUTO_REFRESH_TRIGGER", "Token próximo de expirar");
        await performRefresh();
      }
    } catch (error) {
      log.error("AUTO_REFRESH_ERROR", error);
    }
  }, AUTO_REFRESH_INTERVAL);
};

export const stopAutoRefresh = () => {
  if (autoRefreshInterval) {
    clearInterval(autoRefreshInterval);
    autoRefreshInterval = null;
    log.warn("AUTO_REFRESH_STOP", "Intervalo parado");
  }
};

// ========================================
// INTERCEPTOR DE REQUEST
// ========================================
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url || "";

    if (isPublicEndpoint(url)) {
      return config;
    }

    // CSRF: backend exige x-csrf-token em qualquer mutação não-pública (em
    // localhost a checagem é estrita); endpoints protegidos legados também
    // continuam cobertos. NÃO restrinja para uma whitelist curta — a regra é
    // "toda mutação que não esteja em isPublicEndpoint". Ver
    // wiki/decisions/csrf-double-submit-frontend.md.
    const needsCsrf =
      isCsrfProtectedEndpoint(url) || isMutation(config.method ?? "");

    if (needsCsrf) {
      let csrfToken = csrfService.getToken();
      if (!csrfToken) {
        try {
          await csrfService.fetchToken();
          csrfToken = csrfService.getToken();
        } catch (e) {
          log.error("CSRF_FETCH_FAILED", "Não foi possível obter token CSRF");
        }
      }
      if (csrfToken) {
        config.headers["x-csrf-token"] = csrfToken;
      }
    }

    const token = newTokenService.getAccessToken();

    if (!token) {
      return config;
    }

    if (config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    {
      const method = (config.method ?? "get").toUpperCase();
      const headers = config.headers ?? ({} as Record<string, unknown>);
      const hasCsrfHeader = !!(headers as any)["x-csrf-token"];
      authLogger.log("newApi", `→ ${method} ${config.url ?? ""}`, {
        hasAuthHeader: !!(headers as any).Authorization,
        hasCsrfHeader,
      });
      if (isMutation(config.method ?? "") && !isPublicEndpoint(url) && !hasCsrfHeader) {
        authLogger.warn(
          "newApi",
          `→ ${method} ${config.url ?? ""} SEM x-csrf-token (cache vazio)`,
        );
      }
    }

    // Preventive refresh: SÓ se houver um access token de fato (ainda que expirando).
    // Sem token, isTokenExpiringSoon retorna true (expiry=0) e dispararia refresh
    // em loop infinito a cada request feito antes do login.
    const currentToken = newTokenService.getAccessToken();
    if (
      currentToken &&
      newTokenService.isTokenExpiringSoon(2 * 60) &&
      !isRefreshing
    ) {
      log.warn("REQUEST_PREVENTIVE_REFRESH", "Token expirando em menos de 2min");

      try {
        const newToken = await performRefresh();
        if (config.headers) {
          config.headers.Authorization = `Bearer ${newToken}`;
        }
      } catch (error) {
        log.error("PREVENTIVE_REFRESH_ERROR", error);
      }
    }

    return config;
  },
  (error) => {
    log.error("REQUEST_INTERCEPTOR_ERROR", error.message);
    return Promise.reject(error);
  }
);

// ========================================
// INTERCEPTOR DE RESPONSE
// ========================================
api.interceptors.response.use(
  (response) => {
    const token = response.data?.accessToken || response.data?.access_token || response.data?.token;
    const expiresAt = response.data?.expiresAt;

    if (token && expiresAt) {
      log.info("RESPONSE_TOKEN_UPDATE", "Novo token recebido");
      newTokenService.setTokens(token, expiresAt);

      if (!autoRefreshInterval) {
        startAutoRefresh();
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _csrfRetry?: boolean;
    };

    const url = originalRequest?.url || "";

    log.error("RESPONSE_ERROR", {
      url,
      status: error.response?.status,
      message: (error.response?.data as any)?.message,
    });
    authLogger.warn(
      "newApi",
      `← ${(originalRequest?.method ?? "GET").toUpperCase()} ${url} ERRO`,
      {
        httpStatus: error.response?.status,
        code: (error.response?.data as any)?.code,
        message: (error.response?.data as any)?.message,
        retried: !!originalRequest?._retry,
      },
    );

    if (error.response?.status === 401) {
      if (originalRequest._retry) {
        log.error("401_FINAL_REJECT", "Já fez retry, deslogando");
        clearSessionStorage();
        stopAutoRefresh();
        notifyTokenExpired();
        return Promise.reject(error);
      }

      if (isAuthEndpoint(url) || url.includes("/auth/refresh")) {
        log.warn("401_SKIP_REFRESH", "Endpoint de autenticação");
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      authLogger.log("refresh", `401 em ${url} — disparando refresh + retry`);

      try {
        const newToken = await performRefresh();
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        log.info("401_RETRY", "Tentando requisição novamente");
        return api(originalRequest);
      } catch (refreshError) {
        log.error("401_REFRESH_FAILED", refreshError);
        // Só a recusa do servidor encerra a sessão. Rede, tempo esgotado, 5xx ou um 409 que sobrou falham esta
        // requisição e mantêm a sessão: a próxima chamada renova de novo pelo cookie (2026-10-28).
        if (!refreshEndsSession(refreshError)) {
          return Promise.reject(error);
        }
        clearSessionStorage();
        stopAutoRefresh();
        notifyTokenExpired();
        return Promise.reject(error);
      }
    }

    // A stale CSRF copy is not an ended session: refetch once and repeat (retryPolicy.ts).
    if (
      isCsrfRejection(error) &&
      originalRequest &&
      !originalRequest._csrfRetry &&
      !url.includes("/auth/refresh")
    ) {
      originalRequest._csrfRetry = true;
      try {
        const fresh = await csrfService.fetchToken();
        if (fresh && originalRequest.headers) {
          originalRequest.headers["x-csrf-token"] = fresh;
        }
        return api(originalRequest);
      } catch (csrfError) {
        log.error("CSRF_RETRY_FAILED", csrfError);
      }
    }

    if (error.response?.status === 403) {
      const message = (error.response.data as any)?.message || "";

      if (
        message.includes("Device fingerprint mismatch") ||
        message.includes("Sessão inválida") ||
        message.includes("sessão foi encerrada")
      ) {
        log.error("403_SESSION_REVOKED", message);
        newTokenService.clearTokens();
        stopAutoRefresh();
        notifySessionRevoked();

        // Notifica outras abas sobre a revogação
        sessionSyncService.emit("SESSION_REVOKED");
      }

      // 403 POR HOSPITAL: o backend diz em quais hospitais o próprio usuário TEM
      // a capacidade. Em vez de negar em silêncio, a sessão oferece a troca —
      // quem escuta é o ActiveHospitalProvider (contexto), daí o evento.
      const body = error.response.data as { error?: string } | undefined;
      if (body?.error === FORBIDDEN_AT_HOSPITAL) {
        window.dispatchEvent(
          new CustomEvent(FORBIDDEN_AT_HOSPITAL, { detail: body }),
        );
      }
    }

    return Promise.reject(error);
  }
);

export default api;
export { notifyTokenExpired, notifySessionRevoked, performRefresh, log };
